import { observable } from 'mobx';

export const BAKURAKU_BEFORE_APPLICATION_TITLE = '申請前';

export type BakurakuWorkflowRequestResponseArgs = {
  bakuraku_workflow_request_id: string;
  bakuraku_workflow_request_title?: string | null;
  bakuraku_workflow_request_num?: string | null;
  status?: string | null;
  request_user_name?: string | null;
  request_user_email?: string | null;
  opened_at?: string | null;
};

export type BakurakuWorkflowRequestArgs = {
  bakurakuWorkflowRequestId: string;
  bakurakuWorkflowRequestTitle: string;
  bakurakuWorkflowRequestNum: string;
  status?: string;
  requestUserName?: string;
  requestUserEmail?: string;
  openedAt?: string;
};

export type BakurakuWorkflowRequestSnapshotArgs = {
  bakurakuWorkflowRequestId?: string | null;
  bakurakuWorkflowRequestTitle?: string | null;
  bakurakuWorkflowRequestNum?: string | null;
  status?: string | null;
  requestUserName?: string | null;
  requestUserEmail?: string | null;
  openedAt?: string | null;
};

const formatOpenedAt = (openedAt?: string) => {
  if (!openedAt) {
    return '';
  }

  return openedAt.replace('T', ' ').slice(0, 16);
};

export const convertBakurakuWorkflowRequestResponseToArgs = (
  response: BakurakuWorkflowRequestResponseArgs
): BakurakuWorkflowRequestArgs => ({
  bakurakuWorkflowRequestId: response.bakuraku_workflow_request_id || '',
  bakurakuWorkflowRequestTitle: response.bakuraku_workflow_request_title || '',
  bakurakuWorkflowRequestNum: response.bakuraku_workflow_request_num || '',
  status: response.status || undefined,
  requestUserName: response.request_user_name || undefined,
  requestUserEmail: response.request_user_email || undefined,
  openedAt: response.opened_at || undefined
});

export const formatBakurakuWorkflowRequestLabel = (args?: BakurakuWorkflowRequestSnapshotArgs | null) => {
  if (!args) {
    return '';
  }

  if (args.bakurakuWorkflowRequestTitle) {
    return args.bakurakuWorkflowRequestTitle;
  }

  if (args.bakurakuWorkflowRequestNum) {
    return `申請番号: ${args.bakurakuWorkflowRequestNum}`;
  }

  return args.bakurakuWorkflowRequestId || '';
};

export const formatBakurakuWorkflowRequestCaption = (args?: BakurakuWorkflowRequestSnapshotArgs | null) => {
  if (!args || !formatBakurakuWorkflowRequestLabel(args)) {
    return '';
  }

  const parts = [];
  if (args.bakurakuWorkflowRequestNum) {
    parts.push(`申請番号: ${args.bakurakuWorkflowRequestNum}`);
  }
  if (args.bakurakuWorkflowRequestId) {
    parts.push(`申請ID: ${args.bakurakuWorkflowRequestId}`);
  }
  if (args.status) {
    parts.push(`ステータス: ${args.status}`);
  }
  if (args.openedAt) {
    parts.push(`起票日: ${formatOpenedAt(args.openedAt)}`);
  }
  if (args.requestUserName) {
    parts.push(`申請者: ${args.requestUserName}`);
  }

  return parts.join(' / ');
};

export const buildBeforeBakurakuWorkflowRequest = () =>
  new BakurakuWorkflowRequest({
    bakurakuWorkflowRequestId: '',
    bakurakuWorkflowRequestTitle: BAKURAKU_BEFORE_APPLICATION_TITLE,
    bakurakuWorkflowRequestNum: ''
  });

export const buildBakurakuWorkflowRequestFromSnapshot = (args?: BakurakuWorkflowRequestSnapshotArgs | null) => {
  if (!args) {
    return null;
  }

  const bakurakuWorkflowRequestId = args.bakurakuWorkflowRequestId || '';
  const bakurakuWorkflowRequestTitle = args.bakurakuWorkflowRequestTitle || '';
  const bakurakuWorkflowRequestNum = args.bakurakuWorkflowRequestNum || '';

  if (!bakurakuWorkflowRequestId && !bakurakuWorkflowRequestTitle && !bakurakuWorkflowRequestNum) {
    return null;
  }

  if (!bakurakuWorkflowRequestId && bakurakuWorkflowRequestTitle === BAKURAKU_BEFORE_APPLICATION_TITLE) {
    return buildBeforeBakurakuWorkflowRequest();
  }

  return new BakurakuWorkflowRequest({
    bakurakuWorkflowRequestId,
    bakurakuWorkflowRequestTitle,
    bakurakuWorkflowRequestNum,
    status: args.status || undefined,
    requestUserName: args.requestUserName || undefined,
    requestUserEmail: args.requestUserEmail || undefined,
    openedAt: args.openedAt || undefined
  });
};

export default class BakurakuWorkflowRequest {
  @observable bakurakuWorkflowRequestId: string;
  @observable bakurakuWorkflowRequestTitle: string;
  @observable bakurakuWorkflowRequestNum: string;
  @observable status?: string;
  @observable requestUserName?: string;
  @observable requestUserEmail?: string;
  @observable openedAt?: string;

  constructor(args: BakurakuWorkflowRequestArgs) {
    this.bakurakuWorkflowRequestId = args.bakurakuWorkflowRequestId;
    this.bakurakuWorkflowRequestTitle = args.bakurakuWorkflowRequestTitle;
    this.bakurakuWorkflowRequestNum = args.bakurakuWorkflowRequestNum;
    this.status = args.status;
    this.requestUserName = args.requestUserName;
    this.requestUserEmail = args.requestUserEmail;
    this.openedAt = args.openedAt;
  }

  isBeforeApplication() {
    return (
      !this.bakurakuWorkflowRequestId && this.bakurakuWorkflowRequestTitle === BAKURAKU_BEFORE_APPLICATION_TITLE
    );
  }

  matches(other?: BakurakuWorkflowRequest | null) {
    if (!other) {
      return false;
    }

    return (
      this.bakurakuWorkflowRequestId === other.bakurakuWorkflowRequestId &&
      this.bakurakuWorkflowRequestTitle === other.bakurakuWorkflowRequestTitle &&
      this.bakurakuWorkflowRequestNum === other.bakurakuWorkflowRequestNum
    );
  }

  get label() {
    return formatBakurakuWorkflowRequestLabel(this);
  }

  get caption() {
    return formatBakurakuWorkflowRequestCaption(this);
  }
}
